import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Phone, MapPin, Mail, Loader2, User as UserIcon,
  Clock, PhoneCall, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { setActiveCall } from "@/lib/callState";

const PREVIEW_SECONDS = 20;         // preview countdown before we prompt

/**
 * Pre-call briefing modal. Three phases:
 *
 *   phase="preview"  → 20-second briefing with contact + last-email excerpt
 *   phase="confirm"  → "Iniciar chamada? Sim / Não" pop-up
 *   phase="copied"   → 1.5s confirmation "Número copiado", then navigate to
 *                     /leads/{id}. The floating <CallBanner /> takes over.
 *
 * The call itself is tracked by the backend (``POST /leads/{id}/call/start``)
 * so the timer keeps working across page navigations. See ``@/lib/callState``
 * for the localStorage handshake used by the banner.
 */
export default function LeadPreviewModal({ open, leadId, leadPreview, onClose }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("preview");
  const [lead, setLead] = useState(leadPreview || null);
  const [loading, setLoading] = useState(false);
  const [previewLeft, setPreviewLeft] = useState(PREVIEW_SECONDS);
  const [starting, setStarting] = useState(false);

  // ---- Reset state whenever the modal opens for a fresh lead ----
  useEffect(() => {
    if (!open) return;
    setPhase("preview");
    setPreviewLeft(PREVIEW_SECONDS);
    setStarting(false);
    setLead(leadPreview || null);
    if (leadId) {
      setLoading(true);
      api.get(`/leads/${leadId}`)
        .then((r) => setLead(r.data))
        .catch(() => toast.error("Erro ao carregar dados do lead"))
        .finally(() => setLoading(false));
    }
  }, [open, leadId, leadPreview]);

  // ---- Preview countdown (20s → confirm dialog) ----
  useEffect(() => {
    if (!open || phase !== "preview") return;
    if (previewLeft <= 0) { setPhase("confirm"); return; }
    const t = setTimeout(() => setPreviewLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [open, phase, previewLeft]);

  // ---- Last inbound email (subject + body_preview) ----
  const lastEmail = useMemo(() => {
    if (!lead) return null;
    const arr = lead.inbound_emails || [];
    if (!arr.length) return null;
    // Reconciled emails may arrive out of order, so pick by received_at
    return [...arr].sort((a, b) =>
      (b.received_at || "").localeCompare(a.received_at || "")
    )[0];
  }, [lead]);

  const contact = lead?.qualification?.contact || {};
  const location = lead?.qualification?.location || {};
  const phone = contact.phone || "";

  // ---- Handlers ----
  const startCall = async () => {
    if (!phone) {
      toast.error("Este lead não tem telefone.");
      return;
    }
    if (!lead?.id) return;
    setStarting(true);
    // 1) copy phone to clipboard
    try {
      await navigator.clipboard.writeText(phone);
    } catch { /* silent — the banner exposes a "copiar nº" fallback */ }
    // 2) register call segment in the backend so KPIs + timeline stay honest
    try {
      await api.post(`/leads/${lead.id}/call/start`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // If a segment is already open (race with the banner) just proceed —
      // the banner will pick it up from localStorage.
      if (!detail || !String(detail).toLowerCase().includes("segment already open")) {
        toast.error(detail || "Erro a iniciar chamada");
        setStarting(false);
        return;
      }
    }
    // 3) hand the ball to the global CallBanner via localStorage
    setActiveCall({
      leadId: lead.id,
      leadName: contact.name || "",
      phone,
      startedAt: new Date().toISOString(),
    });
    // 4) confirmation phase — 1.5s "número copiado", then navigate
    setPhase("copied");
    setTimeout(() => {
      onClose?.();
      navigate(`/leads/${lead.id}`);
    }, 1500);
  };

  const cancelCall = () => {
    onClose?.();
  };

  // 20/02/2026 — Novo caminho: abrir o lead sem iniciar chamada.
  // Cobre o caso em que o Atendimento só quer consultar/actualizar
  // dados que não vêm da chamada (ex: código postal, notas internas)
  // sem que o cronómetro de KPI comece a correr.
  const openWithoutCall = () => {
    if (!lead?.id) return;
    onClose?.();
    navigate(`/leads/${lead.id}`);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lead-preview-modal">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-border">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-muted/40 to-card">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading">
              {phase === "preview" && "Briefing antes da chamada"}
              {phase === "confirm" && "Preparado para ligar"}
              {phase === "copied"  && "A abrir o lead…"}
            </div>
            <div className="font-display text-lg text-foreground mt-0.5">
              {loading && !lead ? "A carregar…" : (contact.name || "Lead sem nome")}
            </div>
          </div>
          <button onClick={onClose} data-testid="preview-close-btn" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — phase 1: preview */}
        {phase === "preview" && (
          <div className="p-5 space-y-4">
            {loading && !lead ? (
              <div className="py-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PreviewItem icon={UserIcon} label="Nome" value={contact.name || "—"} testid="preview-name" />
                  <PreviewItem icon={Phone} label="Telefone" value={contact.phone || "—"} testid="preview-phone" />
                  <PreviewItem
                    icon={MapPin}
                    label="Localidade"
                    value={[location.municipality, location.district].filter(Boolean).join(", ") || "—"}
                    testid="preview-location"
                  />
                  <PreviewItem icon={Mail} label="Email" value={contact.email || "—"} testid="preview-email" />
                </div>

                <div className="border border-border rounded-xl bg-muted/40 p-4" data-testid="preview-request">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading flex items-center gap-1.5">
                      <Mail className="w-3 h-3" /> Pedido do cliente
                    </div>
                    {lastEmail?.received_at && (
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(lastEmail.received_at).toLocaleString("pt-PT")}
                      </div>
                    )}
                  </div>
                  {lastEmail ? (
                    <>
                      {lastEmail.subject && (
                        <div className="text-xs font-semibold text-foreground mb-1">
                          {lastEmail.subject}
                        </div>
                      )}
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {(lastEmail.body || "").trim() || "(sem conteúdo)"}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground italic">
                      Este lead não tem emails recebidos.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Countdown */}
            <div className="flex items-center justify-between border-t border-border/60 pt-3 gap-3">
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-1 min-w-0">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                Vou perguntar se queres ligar em <b className="text-foreground tabular-nums ml-1">{previewLeft}s</b>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={openWithoutCall}
                  data-testid="skip-preview-open-btn"
                  className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2"
                >
                  Ver sem ligar
                </button>
                <button
                  onClick={() => setPhase("confirm")}
                  data-testid="skip-preview-btn"
                  className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2"
                >
                  Saltar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Body — phase 2: confirm */}
        {phase === "confirm" && (
          <div className="p-6 space-y-5" data-testid="preview-confirm">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[hsl(var(--info-bg))] border border-[#BFDBFE] flex items-center justify-center">
                <PhoneCall className="w-6 h-6 text-primary" />
              </div>
              <div className="font-display text-xl text-foreground mt-3">Iniciar chamada?</div>
              <div className="text-sm text-muted-foreground mt-1">
                Vamos copiar <b className="text-foreground">{phone || "—"}</b> para o clipboard.
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={startCall}
                disabled={starting}
                data-testid="preview-accept-btn"
                className="w-full py-2.5 rounded-full text-sm font-heading text-primary-foreground bg-primary hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
                Sim, ligar
              </button>
              <button
                onClick={openWithoutCall}
                disabled={starting}
                data-testid="preview-open-without-call-btn"
                className="w-full py-2 rounded-full text-sm font-heading text-foreground bg-card border border-border hover:border-primary hover:text-primary transition-colors disabled:opacity-40"
              >
                Ver sem ligar (só consultar/actualizar)
              </button>
              <button
                onClick={cancelCall}
                disabled={starting}
                data-testid="preview-decline-btn"
                className="w-full py-1.5 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-40"
              >
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* Body — phase 3: 1.5s "número copiado" confirmation before nav */}
        {phase === "copied" && (
          <div className="p-8 text-center" data-testid="preview-copied">
            <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[hsl(var(--success))]" />
            </div>
            <div className="font-display text-xl text-foreground mt-4">Número copiado</div>
            <div className="text-sm text-muted-foreground mt-1">
              <b className="text-foreground font-mono">{phone}</b> · a abrir o lead…
            </div>
            <div className="text-[11px] text-muted-foreground mt-3">
              O cronómetro vai aparecer no canto inferior direito. Clica em <b>Terminei a chamada</b> quando desligares.
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function PreviewItem({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-heading flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm text-foreground mt-1 truncate">{value}</div>
    </div>
  );
}
